import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

/**
 * Verrou personnalisé avec plafond de temps, pour éviter qu'une contention
 * entre onglets ne fige la page.
 *
 * Signature attendue par le client : (name, acquireTimeout, fn) => Promise<R>.
 * Il est essentiel d'exécuter réellement `fn()` et de renvoyer son résultat,
 * sinon les opérations d'authentification (et la récupération du jeton pour
 * les requêtes de données) reçoivent `undefined` et échouent.
 */
function customLock<R>(name: string, acquireTimeout: number, fn: () => Promise<R>): Promise<R> {
  return new Promise<R>((resolve, reject) => {
    let settled = false;

    const runWithoutLock = () => {
      if (settled) return;
      settled = true;
      fn().then(resolve).catch(reject);
    };

    const timeoutMs = acquireTimeout > 0 ? acquireTimeout : 10000;
    const timer = setTimeout(runWithoutLock, timeoutMs);

    if (typeof navigator === 'undefined' || !('locks' in navigator)) {
      clearTimeout(timer);
      runWithoutLock();
      return;
    }

    navigator.locks
      .request(name, { mode: 'exclusive' }, async () => {
        clearTimeout(timer);
        if (settled) return;
        settled = true;
        try {
          resolve(await fn());
        } catch (err) {
          reject(err);
        }
      })
      .catch(() => {
        clearTimeout(timer);
        runWithoutLock();
      });
  });
}

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    lock: customLock,
  },
});